import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import NumberField from 'components/number-field';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { TFunction } from 'i18next';
import { DisabledFields } from 'types/disabledFields';
import { listingItemtype, PartyAccounting } from 'types/ledger';
import { AccountingInvolvedParty, AccountingType } from 'v2-types/order';
import { formatMoney } from 'utils/helpers';

import ListAccounted from './components/list-accounted';
import PartyPicker from './components/party-picker';
import { AddAndUpdatePartyLedgerForm } from './types';

const useStyles = makeStyles((theme: any) => ({
  form: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
  },
  header: {
    flex: '0 0 auto',
    padding: theme.spacing(3, 3, 1),
  },
  title: {
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3, 3),
    [theme.breakpoints.down('md')]: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
  },
  numeric: {
    '& input': {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
  },
  actions: {
    flex: '0 0 auto',
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    // Desktop: align ModalActionBar with the content gutter; mobile keeps its safe-area padding.
    [theme.breakpoints.up('md')]: {
      '& > div': {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      },
    },
  },
  pickerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  // Mobile step 1: the party list takes the remaining height of the full-screen dialog.
  steppedFill: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  fieldsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  createBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  twoColumns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
    gap: theme.spacing(3),
    alignItems: 'start',
  },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  stepHidden: { display: 'none' },
}));

type CreateStep = 'party' | 'details';

type AddAndUpdatePartyLedgerSceneProps = {
  onClose: () => void;
  methods: UseFormReturn<AddAndUpdatePartyLedgerForm, object>;
  t: TFunction;
  handleSubmit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  handleSelectParty: (user: PartyAccounting | null) => void;
  allowedAssociations: listingItemtype[];
  currentPartySelected: PartyAccounting | null;
  errors: string[];
  loadingAddAssociation: boolean;
  loadingEditAssociation: boolean;
  entryLedger: AccountingType;
  partyEntryPrevInfo?: AccountingInvolvedParty;
  orderParties: PartyAccounting[];
  filterParties?: string;
  hideFields?: DisabledFields<AddAndUpdatePartyLedgerForm>;
};

const calculate = (outputtype: 'amount' | 'percentaje', input: number, total: number) => {
  if (outputtype === 'percentaje' && total) {
    return (input * 100) / total;
  }
  if (outputtype === 'amount' && total) {
    return (input * total) / 100;
  }
  return 0;
};

export const AddAndUpdatePartyLedgerScene = ({
  onClose,
  methods,
  entryLedger,
  allowedAssociations,
  handleSubmit,
  handleSelectParty,
  currentPartySelected,
  errors,
  partyEntryPrevInfo,
  t,
  orderParties,
  loadingAddAssociation,
  loadingEditAssociation,
  filterParties,
  hideFields,
}: AddAndUpdatePartyLedgerSceneProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { control, setValue, getValues, watch } = methods;
  const selectedPartyId = watch('party_id');
  const [step, setStep] = useState<CreateStep>('party');
  const ledgerOriginalAmount =
    entryLedger.amount_override && entryLedger.amount_override > 0
      ? entryLedger.amount_override
      : entryLedger.amount;
  const totalAmount = ledgerOriginalAmount || 0;
  const isSubmitting = loadingAddAssociation || loadingEditAssociation;
  const fieldColumns = isMobile ? 12 : 6;
  const showAmountRow = !hideFields?.amount || !hideFields?.percent;
  const showBeforeClosingRow = !hideFields?.before_closing_amount || !hideFields?.before_closing_percent;
  const showAtClosingRow = !hideFields?.at_closing_amount || !hideFields?.at_closing_percent;
  const isCreate = !partyEntryPrevInfo;
  const isStepped = isCreate && isMobile;
  const isTwoColumn = isCreate && !isMobile;
  const hasParty = !!selectedPartyId;
  const hidePicker = isStepped && step !== 'party';
  const hideFieldsSection = isStepped && step !== 'details';

  // Distinct keys remount the bar per step, so the clicked "Next" button never turns into the submit button mid-click.
  const renderActionBar = () => {
    if (isStepped && step === 'party') {
      return (
        <ModalActionBar
          key="party-step"
          onCancel={onClose}
          cancelText={t('common:close')}
          submitText={t('common:next')}
          submitIcon="arrow-right"
          submitType="button"
          submitDisabled={!hasParty}
          onSubmit={() => setStep('details')}
          isSubmitting={isSubmitting}
        />
      );
    }
    if (isStepped) {
      return (
        <ModalActionBar
          key="details-step"
          onCancel={() => setStep('party')}
          cancelText={t('auth:back')}
          cancelIcon="arrow-left"
          submitText={t('accounting:add-association')}
          submitType="submit"
          submitDisabled={!hasParty}
          isSubmitting={isSubmitting}
        />
      );
    }
    return (
      <ModalActionBar
        key="single-step"
        onCancel={onClose}
        cancelText={t('common:close')}
        submitText={partyEntryPrevInfo ? t('accounting:edit-association') : t('accounting:add-association')}
        submitType="submit"
        submitDisabled={isCreate && !hasParty}
        isSubmitting={isSubmitting}
      />
    );
  };

  const fields = (
    <>
      {!hideFields?.kind && (
        <Controller
          name="kind"
          control={control}
          render={({ field: { ref, ...field } }) => (
            <SelectField
              {...field}
              value={allowedAssociations.length > 0 ? field.value : ''}
              inputRef={ref}
              data={allowedAssociations}
              dataKey="code"
              dataValue="code"
              dataText="description"
              label={t('users:kind')}
              disabled={!!partyEntryPrevInfo}
              handleChange={field.onChange}
            />
          )}
        />
      )}
      {!hideFields?.full_amount && (
        <Box className={classes.numeric}>
          <NumberField
            decimalScale={9}
            label={t('accounting:full-amount')}
            thousandSeparator=","
            prefix="$"
            value={totalAmount || 0}
            allowNegative={false}
            disabled
          />
        </Box>
      )}
      {showAmountRow && (
        <Grid container direction="row" spacing={2} className={classes.numeric}>
          {!hideFields?.amount && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="amount"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    customRef={ref}
                    decimalScale={9}
                    label={t('lender:amount')}
                    value={field.value}
                    isAllowed={({ floatValue = 0 }) => floatValue <= totalAmount}
                    handleBlur={field.onBlur}
                    thousandSeparator=","
                    prefix="$"
                    allowNegative={false}
                    onValueChange={(e, { source }) => {
                      // Ignore programmatic updates (source 'prop'); only user edits recalculate.
                      if (source !== 'event') return;
                      field.onChange(e.floatValue);
                      const calculateNumber = calculate(
                        'percentaje',
                        e.floatValue || 0,
                        totalAmount || 0
                      );
                      const before_closing_percent = getValues('before_closing_percent');
                      const at_Closing_percent = getValues('at_closing_percent');
                      if (before_closing_percent > 0 || at_Closing_percent > 0) {
                        const amountBeforeClosing = (e.floatValue ?? 0) * (before_closing_percent / 100);
                        const amountAfterClosing = (e.floatValue || 0) - amountBeforeClosing;
                        setValue('before_closing_amount', amountBeforeClosing);
                        setValue('at_closing_amount', amountAfterClosing);
                      }
                      setValue('percent', calculateNumber || 0);
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {!hideFields?.percent && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="percent"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    customRef={ref}
                    decimalScale={9}
                    label={t('accounting:percent')}
                    suffix="%"
                    isAllowed={({ floatValue = 0 }) => floatValue <= 100}
                    disabled={!totalAmount}
                    value={field.value}
                    handleBlur={field.onBlur}
                    onValueChange={({ floatValue }, { source }) => {
                      if (source !== 'event') return;
                      const percentValue = floatValue ?? 0;
                      field.onChange(floatValue ?? '');
                      const calculateNumber = calculate(
                        'amount',
                        percentValue,
                        totalAmount || 0
                      );
                      const before_closing_percent = getValues('before_closing_percent');
                      const at_Closing_percent = getValues('at_closing_percent');
                      if (before_closing_percent > 0 || at_Closing_percent > 0) {
                        const amountBeforeClosing = calculateNumber * (before_closing_percent / 100);
                        const amountAfterClosing = calculateNumber - amountBeforeClosing;
                        setValue('before_closing_amount', amountBeforeClosing);
                        setValue('at_closing_amount', amountAfterClosing);
                      }
                      setValue('amount', calculateNumber || 0);
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      )}
      {showBeforeClosingRow && (
        <Grid container direction="row" spacing={2} className={classes.numeric}>
          {!hideFields?.before_closing_amount && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="before_closing_amount"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    {...field}
                    customRef={ref}
                    decimalScale={9}
                    value={field.value}
                    handleBlur={field.onBlur}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    label={t('accounting:before-closing-amount')}
                  />
                )}
              />
            </Grid>
          )}
          {!hideFields?.before_closing_percent && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="before_closing_percent"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    {...field}
                    customRef={ref}
                    decimalScale={9}
                    isAllowed={({ floatValue = 0 }) => floatValue <= 100}
                    value={field.value}
                    suffix="%"
                    handleBlur={field.onBlur}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    label={t('accounting:before-closing-percent')}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      )}
      {showAtClosingRow && (
        <Grid container direction="row" spacing={2} className={classes.numeric}>
          {!hideFields?.at_closing_amount && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="at_closing_amount"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    customRef={ref}
                    decimalScale={9}
                    value={field.value}
                    handleBlur={field.onBlur}
                    isAllowed={({ floatValue = 0 }) => floatValue <= getValues('amount')}
                    label={t('accounting:at-closing-amount')}
                    onValueChange={({ floatValue }, { source }) => {
                      if (source !== 'event') return;
                      const amountValue = floatValue ?? 0;
                      field.onChange(floatValue ?? '');
                      const amountSelected = getValues('amount');
                      const calculateNumber = calculate(
                        'percentaje',
                        amountValue,
                        amountSelected
                      );
                      setValue('at_closing_percent', calculateNumber || 0);
                      setValue('before_closing_percent', 100 - calculateNumber);
                      setValue(
                        'before_closing_amount',
                        calculate('amount', 100 - calculateNumber, amountSelected)
                      );
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {!hideFields?.at_closing_percent && (
            <Grid item xs={fieldColumns}>
              <Controller
                name="at_closing_percent"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    customRef={ref}
                    decimalScale={9}
                    value={field.value}
                    handleBlur={field.onBlur}
                    isAllowed={({ floatValue = 0 }) => floatValue <= 100}
                    suffix="%"
                    onValueChange={({ floatValue }, { source }) => {
                      if (source !== 'event') return;
                      const percentValue = floatValue ?? 0;
                      field.onChange(floatValue ?? '');
                      const amountSelected = getValues('amount');
                      const calculateNumber = calculate(
                        'amount',
                        percentValue,
                        amountSelected
                      );
                      setValue('at_closing_amount', calculateNumber || 0);
                      setValue('before_closing_percent', 100 - percentValue);
                      setValue(
                        'before_closing_amount',
                        calculate('amount', 100 - percentValue, amountSelected)
                      );
                    }}
                    label={t('accounting:at-closing-percent')}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      )}
      {partyEntryPrevInfo?.accounted_by && partyEntryPrevInfo?.accounted_by?.length > 0 && (
        <ListAccounted
          list={partyEntryPrevInfo.accounted_by}
          total={partyEntryPrevInfo?.accounted_amount || 0}
        />
      )}
    </>
  );

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      maxWidth={isTwoColumn ? 'md' : 'sm'}
      fullScreen={isMobile}
      aria-labelledby="party-ledger-entry-title"
    >
      <form onSubmit={handleSubmit} className={classes.form}>
        <DialogTitle id="party-ledger-entry-title" className={classes.header}>
          <Typography variant="h6" component="h2" className={classes.title}>
            {partyEntryPrevInfo
              ? `${t('accounting:edit-association-for')} ${entryLedger.description}`
              : `${t('accounting:add-new-party-for')} ${entryLedger.description || ''}`}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.content}>
          {!!errors.length && (
            <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
              {errors.map((error) => (
                <Typography key={error} variant="body2">{error}</Typography>
              ))}
            </Alert>
          )}
          {isCreate ? (
            <Box
              className={clsx(
                isTwoColumn ? classes.twoColumns : classes.createBody,
                isStepped && classes.steppedFill
              )}
            >
              <Box
                className={clsx(
                  classes.pickerSection,
                  isStepped && classes.steppedFill,
                  hidePicker && classes.stepHidden
                )}
                hidden={hidePicker}
                data-testid="party-picker-section"
              >
                <PartyPicker
                  parties={orderParties}
                  selectedPartyId={selectedPartyId}
                  filterParties={filterParties}
                  disabled={isSubmitting}
                  fillHeight={isStepped}
                  onSelect={(party) => {
                    handleSelectParty(party);
                    setValue('party_id', party._id);
                  }}
                />
              </Box>
              <Box
                className={clsx(classes.fieldsSection, hideFieldsSection && classes.stepHidden)}
                hidden={hideFieldsSection}
                data-testid="party-ledger-fields-section"
              >
                {fields}
              </Box>
            </Box>
          ) : (
            <>
              <TextField
                value={partyEntryPrevInfo?.name || ''}
                disabled
                label={t('parties:contact')}
              />
              <Box className={classes.fieldsSection}>{fields}</Box>
            </>
          )}
        </DialogContent>
        <Box className={classes.actions}>
          {renderActionBar()}
        </Box>
      </form>
    </Dialog>
  );
};
